
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { ApiError, Pageable } from '@empathyco/playboard-core-domain'
import type { PropType, Ref, SetupContext } from 'vue'
import type {
  DataTableOption,
  IndexJobDeploymentState,
  IndexJobDTO,
  IndexJobTransformationState,
  Pagination,
} from '../../models'
import type { IndexJobServiceListFilters } from '../../services/interfaces/index-job.service'
import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useIndexJobPivots } from '../../composables/index/use-index-job-pivots'
import { useApiError } from '../../composables/use-api-error'
import { mapActions } from '../../composables/utils'
import { AlignEnum } from '../../enums'
import { capitalizeSentence } from '../../filters/capitalize-sentence.filter'
import { ToolItemAction } from '../../models'
import { INDEX_CONSTS } from '../../store/index/index.consts'
import { DownloadUtils } from '../../utils'
import DataTable from '../crud/data-table.component.vue'
import PaginationBar from '../crud/pagination-bar.component.vue'
import { UiTooltip } from '../keen-ui'
import IndexJobDetailsModal from './index-job-details-modal.component.vue'
import IndexJobStateBadge from './index-job-state-badge.component.vue'

export default defineComponent({
  name: 'IndexJobsTable',
  components: {
    DataTable,
    IndexJobDetailsModal,
    IndexJobStateBadge,
    PaginationBar,
    UiTooltip,
  },
  props: {
    instances: {
      type: Array as PropType<Array<Instance>>,
      required: true,
    },
    filters: {
      type: Object as PropType<IndexJobServiceListFilters>,
      required: true,
    },
    readonly: Boolean,
  },
  setup(
    props: {
      instances: Array<Instance>
      filters: IndexJobServiceListFilters
      readonly: boolean
    },
    { emit }: SetupContext,
  ) {
    const {
      proxy: { $epb, $store },
    } = getCurrentInstance()

    const { getErrorMessage } = useApiError()

    const baseClass = 'epb-index-jobs-table'

    const messages = $epb.messages.tools.index

    const { getPivots, getPivotLabel, getPivotValueLabel } = useIndexJobPivots()

    const detailsModal = ref()

    const loading = ref(false)

    const selectedJob = ref<IndexJobDTO | undefined>(undefined)
    const selectedJobs = ref<Array<IndexJobDTO>>([])
    const previousSelection = ref<Array<IndexJobDTO>>([])

    const interval = ref<ReturnType<typeof setInterval>>()
    const updateCountInterval = ref<ReturnType<typeof setInterval>>()

    const columns = [
      ...(props.instances.length !== 1
        ? [
            {
              id: 'clientId',
              text: messages.fields.clientId.label,
              width: '50%',
            },
          ]
        : []),
      {
        id: 'id',
        text: messages.fields.id.label,
        width: '75%',
      },
      {
        id: 'transformation.feedId',
        text: messages.fields.feedId.label,
      },
      {
        id: 'state',
        text: messages.fields.state.label,
        width: '3.5rem',
        align: AlignEnum.CENTER,
      },
      {
        id: 'transformation',
        text: messages.fields.transformation.label,
      },
      {
        id: 'deployments',
        text: messages.fields.deployments.label,
      },
      {
        id: 'pivots',
        text: messages.fields.pivots.label,
      },
      {
        id: 'transformation.created',
        text: messages.fields.created.label,
      },
      {
        id: 'totalTime',
        text: messages.fields.totalTime.label,
      },
      {
        id: 'options',
        width: '44px',
        align: AlignEnum.CENTER,
      },
    ]

    const jobs: Ref<Array<IndexJobDTO>> = ref([])

    const tableData = computed((): Array<IndexJobDTO | DataTableOption> => {
      return jobs.value.map(job => ({
        ...job,
        options: getJobOptions(job),
      }))
    })

    const noDetailsRowIds = computed<Array<string>>(() =>
      jobs.value.reduce<Array<string>>((result, job) => {
        if (!job.deployments.length) {
          result.push(job.id)
        }
        return result
      }, []),
    )

    const logoErrors = ref({})

    const itemsPerPageOptions = [10, 20, 50]
    const defaultPagination = {
      page: 1,
      itemsPerPage: itemsPerPageOptions[0],
      totalItems: 0,
    }
    const pagination = ref({ ...defaultPagination })

    const { discardJob, downloadFeed, listJobs, reimportJob } = mapActions(
      $store,
      INDEX_CONSTS.moduleName,
      {
        listJobs: INDEX_CONSTS.actions.listJobs,
        discardJob: INDEX_CONSTS.actions.discardJob,
        downloadFeed: INDEX_CONSTS.actions.downloadFeed,
        reimportJob: INDEX_CONSTS.actions.reimportJob,
      },
    )

    const timeToReload = 60

    const intervalTime = ref(timeToReload)

    const resetInterval = (): void => {
      intervalTime.value = timeToReload
    }

    async function fetchData(defaultLoading = true): Promise<void> {
      try {
        loading.value = defaultLoading
        resetInterval()
        const response = await listJobs({
          ...getPaginationData(pagination.value),
          filters: {
            ...props.filters,
            clientIds: getClientIdsFilter(),
          },
        })
        jobs.value = response.results
        pagination.value.totalItems = response.numFound
      } catch (error) {
        emit(
          'error',
          getErrorMessage({
            ...error,
            message: messages.list.error,
          } as ApiError),
        )
      } finally {
        loading.value = false
      }
    }

    function setLogoError(instanceId: string): void {
      logoErrors.value = {
        ...logoErrors.value,
        [instanceId]: true,
      }
    }

    function getLogo(instanceId: string): string {
      const instance = getInstance(instanceId)
      return instance?.logoUrls.imagotype || instance?.logoUrls.isotype || ''
    }

    function getInstanceLabel(instanceId: string): string {
      const instance = getInstance(instanceId)
      return instance?.prettyName || instance?.id
    }

    function getState(job: IndexJobDTO): IndexJobTransformationState | 'PARTIAL_SUCCESS' {
      let result: IndexJobTransformationState | 'PARTIAL_SUCCESS'
      if (job.state === 'FAILED') {
        result = 'FAILURE'
      } else if (job.state === 'INCOMPLETE') {
        result = 'PARTIAL_SUCCESS'
      } else if (job.state !== 'COMPLETED') {
        result = job.state
      } else {
        if (job.transformation.state !== 'SUCCESS') {
          result = job.transformation.state
        } else {
          result = getDeploymentsState(job) ?? 'SUCCESS'
        }
      }
      return result
    }

    function getDeploymentsState(
      job: IndexJobDTO,
    ): IndexJobDeploymentState | 'PARTIAL_SUCCESS' | undefined {
      let result: IndexJobDeploymentState | 'PARTIAL_SUCCESS' | undefined
      if (job.deployments.length) {
        if (job.deployments.every(deployment => deployment.state === 'QUEUED')) {
          result = 'QUEUED'
        } else if (
          job.deployments.some(deployment => deployment.state === 'QUEUED') ||
          job.deployments.some(deployment => deployment.state === 'RUNNING')
        ) {
          result = 'RUNNING'
        } else if (job.deployments.every(deployment => deployment.state === 'SUCCESS')) {
          result = 'SUCCESS'
        } else if (job.deployments.some(deployment => deployment.state === 'SUCCESS')) {
          result = 'PARTIAL_SUCCESS'
        } else {
          result = 'FAILURE'
        }
      }
      return result
    }

    function getStateTooltip(job: IndexJobDTO): string {
      const state = getState(job)
      let result = messages.fields.state.values[state] || state || ''
      if (state === 'RUNNING') {
        const stepLabel =
          job.transformation.state !== 'SUCCESS'
            ? messages.fields.transformation.stateLabel
            : messages.fields.deployments.stateLabel
        result += `<br>(${stepLabel})`
      }
      if (state === 'PARTIAL_SUCCESS') {
        const failedDeploymentsCount = job.deployments.reduce(
          (count, deployment) => (deployment.state === 'FAILURE' ? count + 1 : count),
          0,
        )
        result += `<br>${$epb.utils.compileMessage(messages.errors.deployments.partial, {
          count: failedDeploymentsCount.toString(),
          total: job.deployments.length.toString(),
        })}`
      } else if (state === 'FAILURE') {
        if (job.transformation.state === 'FAILURE') {
          if (job.transformation.errors.length) {
            result += `<br>${job.transformation.errors.join('<br>')}`
          }
        } else {
          result += `<br>${messages.errors.deployments.total}`
        }
      }
      return capitalizeSentence(result)
    }

    function getTransformationHtml(job: IndexJobDTO): string {
      let result: string
      if (job.transformation.state !== 'SUCCESS') {
        result = '-'
      } else {
        result = `${job.transformation.metrics.docsRead} ${messages.fields.transformation.read}<br>${job.transformation.metrics.docsWritten} ${messages.fields.transformation.write}`
      }
      return result
    }

    function getDeploymentsHtml(job: IndexJobDTO): string {
      let result: string
      const state = getDeploymentsState(job)
      if (!state) {
        result = '-'
      } else {
        result = ['SUCCESS', 'FAILURE', 'RUNNING', 'QUEUED']
          .reduce<Array<string>>((result, state) => {
            const stateDeploymentsCount = job.deployments.reduce(
              (count, deployment) => (deployment.state === state ? count + 1 : count),
              0,
            )
            if (stateDeploymentsCount) {
              result.push(`${stateDeploymentsCount} ${messages.fields.state.values[state]}`)
            }
            return result
          }, [])
          .join('<br>')
      }
      return result
    }

    function getTemplatePivots(job: IndexJobDTO): Array<string> {
      return Object.entries(getPivots(job)).map(([pivotKey, pivotValues]) => {
        const pivotLabelText = getPivotLabel(job, pivotKey) || pivotKey || ''
        const pivotLabel = capitalizeSentence(pivotLabelText)
        const pivotValueLabels = pivotValues.map(pivotValue => {
          const pivotValueText = getPivotValueLabel(job, pivotKey, pivotValue) || pivotValue || ''
          return capitalizeSentence(pivotValueText)
        })
        return `${pivotLabel}: ${pivotValueLabels.join(', ')}`
      })
    }

    function onPaginationChange(paginationParam: Pagination): void {
      pagination.value = paginationParam
      fetchData()
    }

    function getClientIdsFilter(): Array<string> {
      if (!props.filters.clientIds || props.filters.clientIds.length === 0) {
        return props.instances.map(instance => getInstance(instance.id).id)
      } else {
        return props.filters.clientIds
      }
    }

    function getInstance(instanceId: string): Instance {
      return props.instances.find(instance => instance.id === instanceId)
    }

    function getPaginationData(paginationParam: Pagination): Pageable {
      return {
        page: {
          start: (pagination.value.page - 1) * pagination.value.itemsPerPage,
          rows: paginationParam.itemsPerPage,
        },
      }
    }

    function showDetails(job: IndexJobDTO): void {
      selectedJob.value = job
      nextTick(() => detailsModal.value.open())
    }

    function onSelectionChange(selection: Array<IndexJobDTO>): void {
      previousSelection.value = [...selectedJobs.value]
      selectedJobs.value = selection
    }

    function onRowClick(job: IndexJobDTO): void {
      selectedJobs.value = [...previousSelection.value]
      showDetails(job)
    }

    async function onOptionClick(option: DataTableOption, job: IndexJobDTO): Promise<void> {
      const isJobSelected = selectedJobs.value.some(selectedJob => selectedJob.id === job.id)
      const jobsToProcess = isJobSelected ? selectedJobs.value : [job]
      const optionId = option.id as string

      try {
        if (optionId === 'download') {
          const results = await executeActions(jobsToProcess, download)
          handleResults(results, jobsToProcess, 'download')
        } else if (optionId === 'reimport') {
          const results = await executeActions(jobsToProcess, reimport)
          handleResults(results, jobsToProcess, 'reimport')
        } else if (optionId === 'discard') {
          await processDiscards(jobsToProcess)
        }
      } catch (error) {
        emit(
          'error',
          getErrorMessage({
            ...error,
            title: messages.fields.options[option.id].error,
          } as ApiError),
        )
      }
    }

    async function processDiscards(jobs: Array<IndexJobDTO>): Promise<void> {
      const jobsToDiscard = jobs.filter(job => job.state !== 'COMPLETED')
      if (jobsToDiscard.length === 0) {
        return
      }
      const results = await executeActions(jobsToDiscard, job => discard(job))
      handleResults(results, jobsToDiscard, 'discard')
    }

    async function executeActions(
      jobs: Array<IndexJobDTO>,
      action: (job: IndexJobDTO) => Promise<IndexJobDTO | string>,
    ): Promise<{
      successes: Array<IndexJobDTO>
      errors: Array<{ job: IndexJobDTO; error: unknown }>
    }> {
      const results = await Promise.allSettled(jobs.map(job => action(job)))
      const successes: Array<IndexJobDTO> = []
      const errors: Array<{ job: IndexJobDTO; error: unknown }> = []

      results.forEach((result, index) => {
        const job = jobs[index]
        if (result.status === 'fulfilled') {
          const jobToAdd = typeof result.value === 'string' ? job : result.value
          successes.push(jobToAdd)
        } else {
          errors.push({ job, error: result.reason })
        }
      })

      return { successes, errors }
    }

    function handleResults(
      results: {
        successes: Array<IndexJobDTO>
        errors: Array<{ job: IndexJobDTO; error: unknown }>
      },
      totalJobs: Array<IndexJobDTO>,
      optionKey: string,
    ): void {
      const { successes, errors } = results
      const optionMessages = messages.fields.options[optionKey]

      if (errors.length) {
        const errorMessage = `${optionMessages.error} (${errors.length}/${totalJobs.length} failed)`
        emit(
          'error',
          getErrorMessage({
            ...(errors[0].error as object),
            title: errorMessage,
          } as ApiError),
        )
      }

      if (successes.length) {
        const successMessage = `${successes.length} ${optionMessages.success}`
        resetTable()
        emit('success', successMessage, false, successes)
      }

      selectedJobs.value = []
    }

    async function discard(job: IndexJobDTO): Promise<IndexJobDTO> {
      const discardedJob = await discardJob({ correlationId: job.correlationId })
      resetTable()
      return discardedJob
    }

    function resetTable(): void {
      pagination.value = { ...defaultPagination }
      fetchData()
    }

    async function download(job: IndexJobDTO): Promise<string> {
      const { clientId, id: jobId } = job
      const downloadUri = await downloadFeed({ clientId, jobId })
      DownloadUtils.downloadFromUri(downloadUri)
      return downloadUri
    }

    async function reimport(job: IndexJobDTO): Promise<IndexJobDTO> {
      const { clientId, id: jobId } = job
      const reimportedJob = await reimportJob({ clientId, jobId })
      resetTable()
      return reimportedJob
    }

    function getJobOptions(job: IndexJobDTO): Array<DataTableOption> {
      const downloadOption: DataTableOption = {
        id: ToolItemAction.Download,
        text: messages.fields.options.download.text,
      }
      if (props.readonly) {
        return [downloadOption]
      } else {
        return [
          ...(job.state !== 'COMPLETED'
            ? [
                {
                  id: 'discard',
                  text: messages.fields.options.discard.text,
                },
              ]
            : []),
          downloadOption,
          {
            id: 'reimport',
            text: messages.fields.options.reimport.text,
          },
        ] as Array<DataTableOption>
        // TODO: add reindex action
      }
    }

    watch(
      () => props.filters,
      () => {
        resetTable()
      },
      {
        deep: true,
      },
    )

    const updateCount = (): void => {
      if (intervalTime.value) {
        intervalTime.value--
      } else {
        resetInterval()
      }
    }

    onMounted((): void => {
      fetchData()
      updateCountInterval.value = setInterval(updateCount, 1000)
      interval.value = setInterval(() => {
        fetchData(false)
      }, timeToReload * 1000)
    })

    onBeforeUnmount(() => {
      clearInterval(interval.value)
      clearInterval(updateCountInterval.value)
    })

    return {
      baseClass,
      messages,
      columns,
      detailsModal,
      loading,
      selectedJob,
      selectedJobs,
      jobs,
      tableData,
      noDetailsRowIds,
      logoErrors,
      resetTable,
      showDetails,
      onSelectionChange,
      onRowClick,
      onOptionClick,
      getState,
      getStateTooltip,
      getTransformationHtml,
      getDeploymentsHtml,
      getTemplatePivots,
      getLogo,
      setLogoError,
      getInstanceLabel,
      onPaginationChange,
      pagination,
      itemsPerPageOptions,
      intervalTime,
      fetchData,
      noDataImageIndex: require('../../assets/media/image/index/no-data-index.svg'),
    }
  },
})
